<script lang="ts">
	/**
	 * Modal shown after AI analysis check upload completes. Lists all analyses created for the trace.
	 * Same table structure as LotAnalyticalList (AnalyticalRow, no delete). Clicking a row navigates
	 * to /analysis?checkanalysis=<publicId> and closes the modal.
	 * "Erneut starten" opens file picker while modal stays in background (blur); cancel keeps modal open.
	 */
	import { modals } from 'svelte-modals'
	import { fly } from 'svelte/transition'
	import { t } from '$i18n/i18n'
	import { onDestroy, onMount } from 'svelte'
	import { fetchUtils } from '$utils/fetchUtils'
	import type { Analytical } from '../map/types/analytical'
	import { AnalyticalParentType, ButtonIcons } from '$types/enums'
	import AnalyticalRow from '../map/fak/lotDetailsLayer/AnalyticalRow.svelte'
	import SecondaryButton, { SecondaryBtnColors } from '../atoms/SecondaryButton.svelte'
	import PrimaryButton from '../atoms/PrimaryButton.svelte'

	export let isOpen: boolean = true
	/** Public IDs of analyses created for this trace (from getPublicIds). */
	export let publicIds: string[] = []
	/** Called when user clicks Cancel. Default: closeModal. */
	export let onCancel: () => void = () => modals.close()
	/** Called when user clicks "Erneut starten". Parent should open file picker (modal stays open, z-index lowered until file selected or cancel). */
	export let onRestart: () => void = () => {}

	let analyticals: Analytical[] = []
	let loading = true
	let loadError = false

	/** True while file picker is open: modal z-index lowered so native file picker appears on top. */
	let filePickerOpen = false
	let filePickerCloseCleanup: (() => void) | null = null
	let delayedShowModalId: ReturnType<typeof setTimeout> | null = null

	function stopWaitingForFile() {
		filePickerOpen = false
		filePickerCloseCleanup?.()
		filePickerCloseCleanup = null
	}

	function handleRestart() {
		filePickerOpen = true
		onRestart()

		const delayMs = 300
		const timeoutId = setTimeout(() => {
			const onFocus = () => {
				delayedShowModalId = setTimeout(() => {
					delayedShowModalId = null
					if (filePickerOpen) stopWaitingForFile()
				}, 80)
			}
			window.addEventListener('focus', onFocus)
			filePickerCloseCleanup = () => {
				clearTimeout(timeoutId)
				window.removeEventListener('focus', onFocus)
				if (delayedShowModalId != null) {
					clearTimeout(delayedShowModalId)
					delayedShowModalId = null
				}
			}
		}, delayMs)
		filePickerCloseCleanup = () => clearTimeout(timeoutId)
	}

	onDestroy(() => stopWaitingForFile())

	onMount(() => {
		loadAnalyses()
	})

	async function loadAnalyses() {
		const validIds = (publicIds ?? []).filter((id): id is string => id != null && id !== '')
		if (!validIds.length) {
			loading = false
			return
		}
		loading = true
		loadError = false
		try {
			const results = await Promise.all(
				validIds.map((id) => fetchUtils.get(`/api/checkanalysis/${id}`, false).catch(() => null))
			)

			analyticals = results.filter((r) => r != null).map((r) => ({ ...r, id: r.analyticalId })) // we use here publicId as id, so we need to map it to analyticalId
		} catch (e) {
			console.error('Error loading analyses', e)
			loadError = true
		} finally {
			loading = false
		}
	}
</script>

{#if isOpen}
	<div
		role="dialog"
		aria-labelledby="analysis-check-result-modal-title"
		class="modal modal-backdrop"
		class:modal--file-picker-open={filePickerOpen}
		in:fly={{ y: -1000, duration: 400 }}
		out:fly={{ y: -1000, duration: 400 }}
	>
		<div class="contents analysis-check-result-contents">
			<div class="modal-body">
				<h1 id="analysis-check-result-modal-title" class="modal-title">Analysen</h1>
				{#if loading}
					<p class="loading-text">{$t('UI.loading')}...</p>
				{:else if loadError}
					<p class="error-text">{$t('UI.FileUploadAI.analysisError')}</p>
				{:else if analyticals.length === 0}
					<p class="empty-text">{$t('UI.map.noAnalyticals.text')}</p>
				{:else}
					<div class="modal-body-scroll-area">
						<div class="analytic-table-header">
							<div>
								{$t('Analytics.testMethod')}
								<br />{$t('Analytics.description')}
							</div>
							<div>
								{$t('Analytics.evaluation')}
								<br />{$t('Analytics.maxValue')}
							</div>
							<div></div>
						</div>
						<div class="analytic-table scrollable">
							{#each analyticals as analytical (analytical.id)}
								<AnalyticalRow
									{analytical}
									analyticalParentType={AnalyticalParentType.CHECK}
									on:updateAnalytical={() => {}}
								/>
							{/each}
						</div>
					</div>
				{/if}
			</div>

			<div class="analysis-modal-footer">
				<SecondaryButton
					label={$t('UI.button.cancel')}
					color={SecondaryBtnColors.GREEN}
					on:click={onCancel}
					sizeAdaptingToText
				/>
				<div class="modal-footer-primary-wrap">
					<PrimaryButton
						label={$t('Analytics.startAgain.btn')}
						leftIcon={ButtonIcons.RELOAD}
						whiteIcon
						fullWidth
						on:click={handleRestart}
					/>
				</div>
			</div>
		</div>
	</div>
{/if}

<style lang="scss">
	@use '../../styles/modals';

	.modal {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		flex-direction: column;
		background: rgba(58, 58, 58, 0.4);
		pointer-events: all;
		z-index: 1001;

		&.modal--file-picker-open {
			z-index: 100;
		}
	}

	.analysis-check-result-contents {
		display: flex;
		flex-direction: column;
		width: 34.375rem;
		max-height: 25.25rem;
		max-width: 100%;
		padding: 0;
		border-radius: 0.25rem;
		overflow: hidden;
		row-gap: 0.625rem;
	}

	.modal-body {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
		padding: 1.5rem 1.5rem 0 1.5rem;
	}

	.modal-body-scroll-area {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.modal-title {
		margin: 0 0 0.625rem 0;
		@include modals.roboto-font(normal, 500, 1.25rem, #000);
		text-align: left;
	}

	.loading-text,
	.empty-text,
	.error-text {
		padding: 1rem 0;
		@include modals.roboto-font(1rem, 400, 1.25rem);
	}

	.error-text {
		color: modals.$primaryRed;
	}

	.analytic-table-header {
		@include modals.roboto-font(normal, 400, 0.75rem);
		display: grid;
		grid-template-columns: 3fr 3fr 1fr;
		column-gap: 0.625rem;
		border: none;
		padding: 0 0 0.625rem 0;
		background: white;
		border-bottom: 1px solid modals.$primaryGreen;
	}

	.analytic-table {
		width: 100%;
		display: grid;
		grid-template-columns: 3fr 3fr 1fr;
		flex: 1;
		min-height: 0;
		max-height: 15.125rem;
		overflow-y: auto;
		scrollbar-width: none;
		-ms-overflow-style: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	.analysis-modal-footer {
		flex-shrink: 0;
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 0.625rem;
		padding: 0 1.5rem 1.5rem;
		background: white;
		align-self: stretch;
	}

	.modal-footer-primary-wrap {
		flex: 1;
		min-width: 0;
		display: flex;
	}
</style>
